import styles from './TripAnnotationTooltip.module.scss'
import { ControlSheetAnnotation, ControlSheetNote } from '../types/types'
import { ControlSheetNotes } from './ControlSheetNotes'
import { clsx } from 'clsx'
import { useCreateTimingAnnotation } from '../hooks/useCreateTimingAnnotation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { validateBusTime } from '../helpers/validateTime'
import { usePatchAnnotation } from '../hooks/usePatchAnnotation'
import { annotationMapper } from '../helpers/annotationMapper'
import { displayTime } from '../helpers/displayTime'

interface TripAnnotationTooltipProps {
    breakpoint: string
    duty: string
    trip: string
    run: string
    route: string
    scheduled: string
    index: number
    contextType: 'Departure' | 'Arrival'
    onUpdate: () => void
    addTempNote: (note: ControlSheetNote) => void
    deleteTempNote: (noteId: string) => void
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void
    annotation?: ControlSheetAnnotation
}

export const TripAnnotationTooltip = (props: TripAnnotationTooltipProps) => {
    const {
        breakpoint,
        duty,
        trip,
        run,
        route,
        scheduled,
        index,
        contextType,
        onUpdate,
        addTempNote,
        deleteTempNote,
        addTempAnnotation,
        annotation,
    } = props

    const annotationIdentifier = `${index}-${scheduled}`

    // State
    const [textValue, setTextValue] = useState<string>('')
    const [checkboxValue, setCheckboxValue] = useState<boolean>(false)
    const [timeValid, setTimeValid] = useState<boolean>(true)
    const [isAnnotationPending, setIsAnnotationPending] = useState(false)

    // Refs
    const textInputRef = useRef<HTMLInputElement>(null)
    const checkboxRef = useRef<HTMLInputElement>(null)
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)
    const pendingCallback = useRef<(() => Promise<ControlSheetAnnotation | undefined>) | null>(null)

    // Hooks
    const { handleCreateTimingAnnotationAsync } = useCreateTimingAnnotation()
    const { handlePatchTimingAnnotationAsync } = usePatchAnnotation()

    // Initialize form values based on annotation
    useEffect(() => {
        if (annotation?.serviceNotOperated) {
            setCheckboxValue(true)
            setTextValue('')
        } else {
            setCheckboxValue(false)
            setTextValue(annotation?.departureTime || '')
        }
    }, [annotation])

    // Focus input on mount
    useEffect(() => {
        if (!checkboxValue && textInputRef.current) {
            textInputRef.current.focus()
            setTimeout(() => {
                textInputRef.current?.select()
            }, 0)
        }
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
            if (pendingCallback.current) {
                pendingCallback.current()
            }
        }
    }, [])

    const createOrPatchAnnotation = useCallback(
        async (
            time: string,
            serviceNotOperated: boolean,
            existingAnnotation?: ControlSheetAnnotation
        ): Promise<ControlSheetAnnotation | undefined> => {
            try {
                if (!existingAnnotation) {
                    const newAnnotationDto = await handleCreateTimingAnnotationAsync(
                        duty,
                        trip,
                        run,
                        route,
                        annotationIdentifier,
                        contextType,
                        serviceNotOperated ? undefined : time,
                        serviceNotOperated
                    )

                    if (newAnnotationDto.id) {
                        const theNewAnnotation = annotationMapper()(newAnnotationDto)
                        return theNewAnnotation
                    }
                } else {
                    const patchedAnnotationDto = await handlePatchTimingAnnotationAsync(
                        existingAnnotation.id,
                        undefined,
                        serviceNotOperated ? undefined : time,
                        serviceNotOperated
                    )

                    if (patchedAnnotationDto.id) {
                        const theNewAnnotation = annotationMapper()(patchedAnnotationDto)
                        return theNewAnnotation
                    }
                }
            } catch (error) {
                console.error(`Error creating or patching annotation: ${error}`)
                throw error
            }
        },
        [
            handleCreateTimingAnnotationAsync,
            handlePatchTimingAnnotationAsync,
            duty,
            trip,
            run,
            route,
            annotationIdentifier,
            contextType,
        ]
    )

    const processAnnotationRequest = useCallback(
        async (time: string, serviceNotOperated: boolean, existingAnnotation?: ControlSheetAnnotation) => {
            setIsAnnotationPending(true)

            let resultAnnotation: ControlSheetAnnotation | undefined
            try {
                resultAnnotation = await createOrPatchAnnotation(time, serviceNotOperated, existingAnnotation)

                if (resultAnnotation) {
                    addTempAnnotation(resultAnnotation)
                }
            } catch (error) {
                console.error(`Failed to process annotation request: ${error}`)
            } finally {
                setIsAnnotationPending(false)
            }
            return resultAnnotation
        },
        [createOrPatchAnnotation, addTempAnnotation]
    )

    const hasAnnotationChanged = useCallback(
        (time: string, serviceNotOperated: boolean): boolean => {
            if (!annotation && time === '' && !serviceNotOperated) {
                return false
            }

            if (annotation) {
                return annotation.departureTime !== time || annotation.serviceNotOperated !== serviceNotOperated
            }

            return true
        },
        [annotation]
    )

    const updateDebounced = useCallback(
        (departure: string, serviceNotOperated: boolean) => {
            // Clear existing timer
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }

            // Only proceed if time is valid and values have changed
            if (!timeValid || !hasAnnotationChanged(departure, serviceNotOperated)) {
                pendingCallback.current = null
                return
            }

            // Create and store callback
            const callback = async () => {
                pendingCallback.current = null
                return await processAnnotationRequest(departure, serviceNotOperated, annotation)
            }

            pendingCallback.current = callback
            debounceTimer.current = setTimeout(callback, 3000)
        },
        [timeValid, hasAnnotationChanged, processAnnotationRequest, annotation]
    )

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTimeValid(true)
        setTextValue(e.target.value.trim())
    }

    const handleTextFocus = () => {
        setTimeValid(true)
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }
    }

    const handleTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()

        if (value.length > 0) {
            const validatedTime = validateBusTime(value)
            if (validatedTime) {
                setTextValue(validatedTime)
                updateDebounced(validatedTime, checkboxValue)
            }
            setTimeValid(validatedTime !== null)
        } else {
            setTextValue('')
            updateDebounced('', checkboxValue)
            setTimeValid(true)
        }
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckboxValue(e.target.checked)
        updateDebounced(textValue, e.target.checked)
    }

    const handleCreateAnnotationForNotes = async () => {
        // Clear any pending debounced updates
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        if (pendingCallback.current) {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
            return await pendingCallback.current()
        }

        setIsAnnotationPending(true)

        try {
            const annotationDto = await handleCreateTimingAnnotationAsync(duty, trip, run, route, annotationIdentifier)
            return annotationMapper()(annotationDto)
        } catch (error) {
            console.error(`Failed to create annotation for notes: ${error}`)
            throw new Error('Failed to create annotation')
        } finally {
            setIsAnnotationPending(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <span className={styles.headerTitle}>{breakpoint}</span>
                <span className={styles.headerSubtitle}>
                    Trip {trip} Run {run}
                </span>
            </div>

            <div className={styles.times}>
                <span className={styles.timesHeader}>Departure times</span>

                <div
                    className={clsx(styles.departureContainer, {
                        [styles.disabled]: checkboxValue,
                    })}
                >
                    <span>Scheduled departure</span>
                    <span>{displayTime(scheduled)}</span>
                </div>

                <div
                    className={clsx(styles.departureContainer, {
                        [styles.disabled]: checkboxValue,
                    })}
                >
                    <span>Amended departure</span>
                    <span>
                        {!checkboxValue ? (
                            <input
                                ref={textInputRef}
                                type="text"
                                value={textValue}
                                maxLength={5}
                                onChange={handleTextChange}
                                onFocus={handleTextFocus}
                                onBlur={handleTextBlur}
                                placeholder="Time"
                                disabled={checkboxValue || isAnnotationPending}
                                className={clsx(styles.textField, {
                                    [styles.invalid]: !timeValid,
                                })}
                            />
                        ) : (
                            '-'
                        )}
                    </span>
                </div>

                {!timeValid && <div className={styles.invalidWarning}>Please enter a valid time</div>}

                <div className={clsx(styles.departureContainer, styles.serviceNotOperated)}>
                    <span>Service not operated</span>
                    <span>
                        <input
                            ref={checkboxRef}
                            type="checkbox"
                            checked={checkboxValue}
                            disabled={isAnnotationPending}
                            onChange={handleCheckboxChange}
                        />
                    </span>
                </div>
            </div>

            <div className={styles.notesContainer}>
                <ControlSheetNotes
                    createAnnotation={handleCreateAnnotationForNotes}
                    annotationId={annotation?.id}
                    existingNotes={annotation?.notes || []}
                    onUpdate={onUpdate}
                    addTempNote={addTempNote}
                    deleteTempNote={deleteTempNote}
                    addTempAnnotation={addTempAnnotation}
                    isAnnotationPending={isAnnotationPending}
                />
            </div>
        </div>
    )
}
